// live/LiveDiagnose.tsx (GH3) - the LIVE Diagnose surface. Every entry deals a FRESH engine hand: you read
// the villain (Axis B), lock it, then face the TRUE legal option set (never a template) and get a REAL
// two-axis grade + template coach text. Reuses <ReadLockIn>, <MathAid>, <BetMathBuilder> (via
// <LiveActionRamp>), <GradeReveal>, and <EndOfHandReport> verbatim.
//
// Fold behavior (spec, locked 2026-07-13): a fold ENDS the hand → grade it → into the report → next hand
// (no forced watching). A WRONG fold also surfaces the OPTIONAL, clearly-labeled counterfactual-replay
// stub (GH6 seam - the replay itself is phase-2, not built here).
import { useEffect, useMemo, useRef, useState } from 'react';
import { initialLock, lockReducer } from '@postflop/engine';
import type { ReadLock } from '@postflop/schema';
import { CinematicFelt } from '../components/CinematicFelt';
import { streamFor } from '../cinematic/streamFor';
import { narrateHand } from '../cinematic/narrate';
import { heroDecisionFrame, heroActionFrame } from '../cinematic/lens';
import { StreetStepper } from '../components/StreetStepper';
import { ReadLockIn } from '../diagnose/ReadLockIn';
import { MathAid, type AidLevel } from '../diagnose/MathAid';
import type { PlayerTag } from '../diagnose/data';
import { GradeReveal } from '../grade/GradeReveal';
import type { ResolvedGrade } from '../grade/types';
import { EndOfHandReport } from '../report/EndOfHandReport';
import { persistPlayedHand } from '../progress/persistPlay';
import type { RampAction } from '../engine/liveHand';
import { buildFoldedWalk, canWatchFold, type Counterfactual } from '../engine/liveRunner';
import { ReplayTimeline } from '../replay/ReplayTimeline';
import { LiveActionRamp } from './LiveActionRamp';
import { LIVE_FAMILIES, buildLiveReport, type LiveReportEntry } from './liveView';
import { stepViewFor } from './stepView';
import { preflopContextLine } from './preflopRecap';
import { buildFoldedObservation, type FoldedObservation } from './foldedObservation';
import { FoldedObservationView } from './FoldedObservationView';
import type { LiveSurfaceProps } from './LiveObserve';

type Phase = 'read' | 'graded';

/** Honesty label for the counterfactual (Fold-behavior spec): "what would've happened if you'd called",
 *  kept distinct from "what really happened". The verb tracks the engine's actual recommended action. */
const IF_YOUD: Record<Counterfactual['correctAction'], string> = {
  call: "if you'd called", raise: "if you'd raised", bet: "if you'd bet", jam: "if you'd shoved",
  check: "if you'd checked", fold: "if you'd folded",
};

// TB2: the hand is the SESSION's (App holds the table); it already carries hero's real played preflop line
// (steps[0]), so this surface reads the same model Observe and Solo do.
export function LiveDiagnose({ model, onNextHand, onLockRead, seatTags, onConfirmReads, onHeroDecision }: LiveSurfaceProps) {
  const [i, setI] = useState(0);
  const step = model.steps[i];
  const isLast = i === model.steps.length - 1;
  // the unified per-step view - preflop and postflop steps render one flow (see live/stepView.ts).
  const sv = useMemo(() => stepViewFor(step, model), [step, model]);

  // CX4 - the ONE renderer. The cinematic felt replaces the static <OvalFelt view={sv.felt}>: the hand plays
  // out action by action and HALTS at each of hero's decisions for him to act. `stream`/`beats` are rebuilt
  // whenever the model does (every played decision rebuilds it, extending the played prefix); the felt is not
  // remounted within a hand (keyed on handId) so its stepper flows on from where it rested. The DIAGNOSE
  // narration lens speaks the villain reads in the moment but never hero's own decision - that is hero's job,
  // and his coaching is the grade.
  const stream = useMemo(() => streamFor(model), [model]);
  const beats = useMemo(() => narrateHand(stream, model, { lens: 'diagnose' }), [stream, model]);
  // the frame the felt is currently showing, reported by <CinematicFelt onIndex>. It tells us whether the
  // action has ARRIVED at hero (the felt reached his decision frame) so the ramp is armed only then, not
  // while villains are still acting into the pot.
  const [feltIndex, setFeltIndex] = useState(0);
  // GH9: on the flop-first entry ground the postflop spot with a one-line preflop recap. Suppressed once
  // preflop is a real playable step (PF4) - the user just played it, so the recap would be redundant.
  const pfctx = useMemo(
    () => (!model.preflop && step.kind === 'postflop' && step.streetIndex === 1 ? preflopContextLine(model) : null),
    [model, step],
  );

  const [tag, setTag] = useState<PlayerTag | null>(null);
  const [weights, setWeights] = useState<number[]>(LIVE_FAMILIES.map(() => 0));
  const [lock, setLock] = useState<ReadLock>(initialLock);
  const [phase, setPhase] = useState<Phase>('read');
  const [grade, setGrade] = useState<ResolvedGrade | null>(null);
  const [aidLevel, setAidLevel] = useState<AidLevel>('full');
  const [committedFold, setCommittedFold] = useState(false);
  const [view, setView] = useState<'stepper' | 'report' | 'counterfactual' | 'watch'>('stepper');
  const [cf, setCf] = useState<Counterfactual | null>(null);
  // TB4: the hand you folded out of, played on and narrated. Built on demand (the walk is a real engine
  // re-advance), so folding costs nothing until you ask to watch.
  const [obs, setObs] = useState<FoldedObservation | null>(null);
  // accumulated per-step grades, keyed by step index (preflop = 0, then the postflop streets).
  const [results, setResults] = useState<(LiveReportEntry | null)[]>(() => model.steps.map(() => null));

  const locked = lock.state === 'lockedIn';
  const persistedRef = useRef(false);

  // reset the read on every street change.
  useEffect(() => {
    setTag(null);
    setWeights(LIVE_FAMILIES.map(() => 0));
    setLock(initialLock());
    setPhase('read');
    setGrade(null);
    setCommittedFold(false);
    setCf(null);
    setObs(null);
    window.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  const commit = (key: RampAction['key'], amount?: string) => {
    const resolved = sv.grade(tag, key, amount);
    setGrade(resolved);
    setResults((r) => {
      const next = r.slice();
      next[i] = { grade: resolved, street: step.street };
      return next;
    });
    setCommittedFold(key === 'fold');
    setPhase('graded');
    // FULL: grading your decision is not the same as PLAYING it. This is the line that plays it - App
    // rebuilds the hand around your action, so the turn you see next is the turn your action produced and
    // the stack you carry to the next hand is the one you actually won or lost. Graded THEN played, in that
    // order: `sv` is built from this step, which your action cannot retroactively change.
    onHeroDecision?.(i, key, amount);
    window.scrollTo({ top: 0 });
  };

  const openReport = () => {
    setView('report');
    window.scrollTo({ top: 0 });
    if (!persistedRef.current) {
      persistedRef.current = true;
      void persistPlayedHand(
        results.map((e) =>
          e ? { gradeClass: e.grade.verdict.cls, skillClasses: { value_extraction: e.grade.axisA.cls, player_profiling: e.grade.axisB.cls } } : null,
        ),
        'guided',
      ).then((res) => { if (!res.ok) console.warn('[progress] live diagnose persist failed:', res.error); });
    }
  };

  /** TB2: advance the SESSION (same seats, carried stacks, rotated button). App remounts this surface on
   *  the new hand number, which is what actually clears the state below - the local resets are kept as a
   *  belt-and-braces so a stale mount can never show hand N's grades over hand N+1's felt. */
  const nextHand = () => {
    persistedRef.current = false;
    setResults([]);
    setCf(null);
    setView('stepper');
    setI(0);
    onNextHand();
  };

  // GH6: build the counterfactual (the engine's recommended line replayed to showdown) on demand - only
  // when the user taps "see how it would've played out". Deterministic in (model, i), so this is a pure
  // engine re-advance, no API call.
  const openCounterfactual = () => {
    const built = sv.counterfactual();
    if (!built) return; // nothing to show (engine also folds); the button is only shown when there is
    setCf(built);
    setView('counterfactual');
    window.scrollTo({ top: 0 });
  };
  /**
   * TB4: you folded - now watch the table finish the hand without you.
   *
   * The locked fold behavior is untouched (grade → report → next, "no forced watching"): this is an OPTIONAL
   * lane off the grade, exactly like the GH6 counterfactual next to it. But it is a different question, and
   * the two must not be confused - the counterfactual is a HYPOTHETICAL ("what if you'd called"), while this
   * is what ACTUALLY happened at the table you are still sitting at. It is offered on every fold, right or
   * wrong, because a correct fold teaches just as much from the rail as a bad one.
   *
   * The showdown (when there is one) writes back to the SESSION's persistent SeatRead, so a read you bought
   * for free out of a pot you were not in is still on that man on hand 9.
   */
  const openWatch = () => {
    const built = buildFoldedWalk(model, step.state);
    if (!built) return; // nothing to watch - the button is only shown when there is.
    const o = buildFoldedObservation(model, built, seatTags ?? new Map());
    setObs(o);
    if (o.evidence.length > 0) onConfirmReads?.(o.evidence);
    setView('watch');
    window.scrollTo({ top: 0 });
  };
  // is there anything to watch? The CHEAP predicate - `buildFoldedWalk` is a full engine walk and must not
  // run speculatively on every render just to decide whether to draw a button.
  const canWatch = useMemo(() => canWatchFold(model, step.state), [model, step]);

  // Clear the scorecard when a NEW HAND arrives - keyed on `handId`, NOT on the model object.
  //
  // FULL: identical to the note in LiveSolo, and it was missed here. Every decision hero plays REBUILDS the
  // model (`applyHeroDecision` returns a new one), so an identity-keyed reset fires the instant he acts and
  // wipes the grade he just earned - the Diagnose hand report closed on an empty scorecard. `handId` is
  // stable across a rebuild and changes on the next hand, which is the question this effect is actually
  // asking. Deliberately NOT keyed on `steps.length` either: 3-betting into a 4-bet legitimately grows the
  // hand by a node, and that is not a new hand.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setResults(model.steps.map(() => null)); }, [model.handId]);

  const reportModel = useMemo(
    () => buildLiveReport(results.filter((e): e is LiveReportEntry => e !== null), 'diagnose'),
    [results],
  );

  if (view === 'watch' && obs) {
    return (
      <FoldedObservationView
        obs={obs}
        onBack={() => { setView('stepper'); window.scrollTo({ top: 0 }); }}
        onReport={openReport}
      />
    );
  }

  if (view === 'counterfactual' && cf) {
    return (
      <>
        <div className="body">
          <div className="cf-head">
            <span className="cf-tag">Counterfactual</span>
            <div className="h2">What would&apos;ve happened {IF_YOUD[cf.correctAction]}</div>
            <div className="lede">
              The engine&apos;s line, replayed to showdown - the hypothetical, not what really happened.{' '}
              {cf.heroWon
                ? `The correct line takes down $${cf.heroNetChips}.`
                : 'This runout does not get there, but the call was still the higher-EV play.'}
            </div>
          </div>
          <ReplayTimeline segments={cf.segments} sourceLabel="Counterfactual" />
        </div>
        <div className="dock">
          <div className="dockrow">
            <button type="button" className="cta ghost prev" onClick={() => { setView('stepper'); window.scrollTo({ top: 0 }); }}>
              Back to the grade
            </button>
            <button type="button" className="cta" onClick={openReport}>
              See the hand report
            </button>
          </div>
        </div>
      </>
    );
  }

  if (view === 'report') {
    return (
      <>
        <div className="body">
          <EndOfHandReport model={reportModel} />
        </div>
        <div className="dock">
          <div className="dockrow">
            <button type="button" className="cta ghost prev" onClick={() => { setView('stepper'); window.scrollTo({ top: 0 }); }}>
              Back
            </button>
            <button type="button" className="cta" onClick={nextHand}>
              Play another hand
            </button>
          </div>
        </div>
      </>
    );
  }

  // a fold ends the hand; a graded non-fold on the last street also ends it. Otherwise step on.
  const endsHand = committedFold || isLast;
  // FULL: stepping preflop→preflop (he 3-bet, a villain came back over the top) is NOT a new street, and the
  // dock must not claim it is. Read off the REBUILT model, which is the only thing that knows the node his
  // action just created exists.
  const advanceLabel = model.steps[i + 1]?.kind === 'preflop' ? 'Next decision' : 'Next street';
  // a wrong fold (graded poorly) surfaces the GH6 counterfactual-replay seam. Only offer the replay when
  // the engine actually has a better line than folding (bookAction !== 'fold'), so the tap always leads
  // somewhere - if the engine agrees with the fold there is nothing to replay.
  const wrongFold = committedFold && grade !== null && grade.axisA.cls !== 'best' && grade.axisA.cls !== 'exc';
  const canReplay = wrongFold && sv.canReplayFold;

  // CX4 - where the felt HALTS. While reading, it stops at hero's decision frame; once graded it advances one
  // frame to SHOW the action hero took (and sit on the grade), or plays out to the showdown/award if the hand
  // is over. `arrived` = the felt has reached hero's decision, so the ramp is armed (he cannot act into a pot
  // the action has not reached yet).
  const decisionFrame = heroDecisionFrame(step);
  const haltAt = phase === 'read' ? decisionFrame : endsHand ? stream.frames.length - 1 : heroActionFrame(step, stream);
  const arrived = feltIndex >= decisionFrame;

  return (
    <>
      <CinematicFelt
        key={model.handId}
        stream={stream}
        beats={beats}
        haltAt={haltAt}
        autoplay
        controls={false}
        onIndex={setFeltIndex}
        // CX-POLISH - resume at the last-shown frame when this felt is remounted after a report /
        // counterfactual / folded-watch excursion (those views unmount it). feltIndex survives the excursion
        // (LiveDiagnose stays mounted; only the felt unmounts), so the return mount picks up where it rested
        // instead of re-running the hand from frame 0. On the first mount of a hand feltIndex is 0, so the
        // action still autoplays TO hero normally.
        startAt={feltIndex}
      />

      <div className="body">
        <div>
          <div className="kicker">{sv.headline.kicker}</div>
          <div className="h2">{sv.headline.h2}</div>
          <div className="lede">{sv.headline.lede}</div>
        </div>

        {pfctx && phase === 'read' && (
          <div className="pfctx">
            <span className="pfctx-k">How the pot got here</span>
            <span className="pfctx-t">{pfctx}</span>
          </div>
        )}

        <StreetStepper street={sv.streetIndex} onSelect={() => { /* live hands are stepped by committing */ }} />

        {phase === 'read' ? (
          <>
            <ReadLockIn
              tagSub={sv.headline.tagSub}
              families={sv.families}
              tag={tag}
              weights={weights}
              locked={locked}
              onTag={setTag}
              onCycleWeight={(fi) => setWeights((w) => w.map((v, j) => (j === fi ? (v + 1) % 4 : v)))}
              onLock={() => {
                setLock((l) => lockReducer(l, { type: 'lockIn', atDecisionIndex: i }));
                // TB3: locking a read writes it to the seat on the SESSION, so it is still on that pod on
                // hand 9. The lock button already gates on a tag being chosen, so `tag` is non-null here.
                // TB5: persist to the villain of THIS node (`step.villainSeat`), the same man the question
                // asked about and Axis B graded - not `model.villainSeat`, the hand-level subject, which on a
                // 3-bet/4-bet hand is the opener who folded, so the tag would land on the wrong pod.
                if (tag) onLockRead?.(step.villainSeat, tag);
              }}
              onRevise={() => setLock((l) => lockReducer(l, { type: 'edit' }))}
            />
            <MathAid price={sv.price} level={aidLevel} onLevel={setAidLevel} />
          </>
        ) : (
          grade && <GradeReveal grade={grade} />
        )}
      </div>

      <div className="dock">
        {phase === 'read' ? (
          <LiveActionRamp
            actions={sv.ramp}
            bet={sv.betDef}
            locked={locked}
            priceReady={arrived}
            hint={!locked ? 'Study the table, then lock your read.' : !arrived ? 'The action is coming to you…' : undefined}
            onCommit={commit}
          />
        ) : (
          <div className="dockcol">
            {/* TB4: offered on EVERY fold, right or wrong - a correct fold teaches from the rail too. Kept
                distinct from the counterfactual below it: this is what really happened, that is a what-if. */}
            {committedFold && canWatch && (
              <button type="button" className="cta ghost cf-open" onClick={openWatch}>
                Watch the table without you
              </button>
            )}
            {canReplay && (
              <button type="button" className="cta ghost cf-open" onClick={openCounterfactual}>
                See how it would&apos;ve played out
              </button>
            )}
            <div className="dockrow">
              {endsHand ? (
                <button type="button" className="cta" onClick={openReport}>
                  See the hand report
                </button>
              ) : (
                <button type="button" className="cta" onClick={() => setI(i + 1)}>
                  {advanceLabel}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
